import { useEffect, useState } from "react";
import axios from "axios";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { Briefcase, ArrowsClockwise } from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API, Loading } from "@/components/mai/entry/entryShared";
import { CaseOverview } from "@/components/mai/entry/CaseOverview";
import { CaseEvidence } from "@/components/mai/entry/CaseEvidence";
import { CaseTransferability } from "@/components/mai/entry/CaseTransferability";
import { CasePathway } from "@/components/mai/entry/CasePathway";
import { CaseStakeholders } from "@/components/mai/entry/CaseStakeholders";
import { CaseEconomics } from "@/components/mai/entry/CaseEconomics";
import { CaseGaps } from "@/components/mai/entry/CaseGaps";
import { CaseActions, CaseTimeline } from "@/components/mai/entry/CaseActions";

const TABS = ["Overview", "Evidence", "Transferability", "Pathway", "Stakeholders", "Economics", "Gaps", "Actions", "Timeline"];

/* ACCESS CASE = PRODUCT × INDICATION × TARGET MARKET × ACCESS OBJECTIVE.
   Core orchestration object: one product, many cases, each with its own
   readiness profile. Evidence lives once at product level. */
export default function AccessCasesPage() {
  const { product, productId, products, setProductId } = useProduct();
  const [cases, setCases] = useState(null);
  const [caseId, setCaseId] = useState(null);
  const [ov, setOv] = useState(null);
  const [otherCases, setOtherCases] = useState(null);

  useEffect(() => {
    setCases(null); setCaseId(null); setOv(null);
    axios.get(`${API}/access-cases`, { params: { product_id: productId } })
      .then((r) => {
        setCases(r.data.cases);
        if (r.data.cases.length) setCaseId(r.data.cases[0].id);
      }).catch(console.error);
  }, [productId]);

  useEffect(() => {
    if (!caseId) return;
    setOv(null);
    axios.get(`${API}/access-cases/${caseId}`).then((r) => setOv(r.data)).catch(console.error);
  }, [caseId]);

  useEffect(() => {
    if (!cases || cases.length > 0 || otherCases || !products.length) return;
    axios.get(`${API}/access-cases`).then((r) => {
      const byProduct = {};
      r.data.cases.forEach((c) => { (byProduct[c.product_id] = byProduct[c.product_id] || []).push(c); });
      setOtherCases(products.filter((p) => byProduct[p.id]).map((p) => ({ product: p, count: byProduct[p.id].length })));
    }).catch(() => setOtherCases([]));
  }, [cases, products, otherCases]);

  const proj = ov?.case;
  return (
    <PageShell pageId="access-cases" overline={`Assess · ${product?.name || ""}`} title="Access Cases">
      <div data-testid="access-cases-page">
        {cases?.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="case-switcher">
            {cases.map((c) => (
              <button key={c.id} onClick={() => setCaseId(c.id)} data-testid={`case-chip-${c.id}`}
                className={`px-4 py-2 text-xs font-bold border transition-colors ${caseId === c.id
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-900"}`}>
                {c.name}
              </button>
            ))}
          </div>
        )}

        {ov?.status === "ok" && proj && (
          <div className="bg-slate-900 text-white px-6 py-5" data-testid="case-header">
            <div className="flex items-start gap-3">
              <Briefcase size={20} className="text-emerald-400 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="font-heading font-bold text-sm">{proj.name}</p>
                <p className="text-xs text-white/60 mt-1 leading-relaxed">
                  {proj.technology} · {proj.indication} · Target market: {ov.target_market?.name}
                </p>
                <p className="text-[11px] text-emerald-400/90 mt-1.5">{proj.access_objective}</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 mt-4 pt-4 border-t border-white/10">
              {[["Technology type", proj.technology_type], ["Target population", proj.target_population],
                ["Regulatory status", proj.regulatory_status], ["Reimbursement status", proj.reimbursement_status]].map(([l, v]) => (
                <div key={l}>
                  <p className="text-[9px] uppercase tracking-widest text-white/40">{l}</p>
                  <p className="text-[11px] text-white/80 leading-snug">{v || "—"}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {(!cases || (cases.length > 0 && !ov)) && <Loading label="Loading access case…" />}

        {cases?.length === 0 && (
          <div className="border border-dashed border-slate-300 bg-white px-6 py-14 text-center" data-testid="no-case-state">
            <Briefcase size={24} className="text-slate-300 mx-auto" />
            <p className="text-sm font-semibold text-slate-700 mt-3">No access case defined for {product?.name}</p>
            <p className="text-xs text-slate-400 mt-1.5 max-w-md mx-auto leading-relaxed">
              An Access Case is Product × Indication × Target Market × Access Objective. It answers: "We have this
              global evidence package and want access in Country X — how ready are we, what can we reuse,
              what is missing, who needs to be involved, and what should we do next?"
            </p>
            {otherCases?.length > 0 && (
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {otherCases.map(({ product: p, count }) => (
                  <button key={p.id} onClick={() => setProductId(p.id)} data-testid={`switch-case-${p.id}`}
                    className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-xs font-bold px-4 py-2 hover:bg-slate-700 transition-colors">
                    <ArrowsClockwise size={12} /> {p.name} ({count} case{count > 1 ? "s" : ""})
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {ov?.status === "ok" && caseId && (
          <Tabs defaultValue="Overview" className="mt-6" key={caseId}>
            <TabsList className="bg-slate-100 flex-wrap h-auto">
              {TABS.map((t) => (
                <TabsTrigger key={t} value={t} data-testid={`case-tab-${t.toLowerCase()}`}
                  className="data-[state=active]:bg-slate-900 data-[state=active]:text-white text-xs">{t}</TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value="Overview"><CaseOverview ov={ov} /></TabsContent>
            <TabsContent value="Evidence"><CaseEvidence caseId={caseId} /></TabsContent>
            <TabsContent value="Transferability"><CaseTransferability caseId={caseId} /></TabsContent>
            <TabsContent value="Pathway"><CasePathway caseId={caseId} /></TabsContent>
            <TabsContent value="Stakeholders"><CaseStakeholders caseId={caseId} /></TabsContent>
            <TabsContent value="Economics"><CaseEconomics caseId={caseId} /></TabsContent>
            <TabsContent value="Gaps"><CaseGaps caseId={caseId} /></TabsContent>
            <TabsContent value="Actions"><CaseActions caseId={caseId} /></TabsContent>
            <TabsContent value="Timeline"><CaseTimeline caseId={caseId} /></TabsContent>
          </Tabs>
        )}
      </div>
    </PageShell>
  );
}
